import { Box, Typography, Stack, Paper, Divider, Alert } from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import type { VisitDetail } from '../../../visits/entity';
import { FormattedMessage } from 'react-intl';
import { ServiceDetailedMetricsTable } from './ServiceDetailedMetricsTable';
import MonthlyTrendChart, { OverheadCost } from './MonthlyTrendChart';
import { useAllPricingCalculatorsQuery } from '../queries';
import { useSuspenseVisitsQuery } from '../../../visits/queries';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useBusinessMetricsData } from '../useBusinessMetricsData';
import { formatPercentage } from '@shared/entity';
import { useFormatCurrency } from '@features/currency/currency.hooks';
import { CustomerRetentionMetrics } from '../entity';

type BusinessMetricsSectionProps = {
  visits: VisitDetail[];
  overheadCosts: OverheadCost[];
  dateRange: { from: Date; to: Date };
  clientsRetention?: CustomerRetentionMetrics;
};

export const BusinessMetricsSection = ({
  visits,
  overheadCosts,
  dateRange,
  clientsRetention,
}: BusinessMetricsSectionProps) => {
  const metrics = useBusinessMetricsData({ visits, overheadCosts, dateRange });
  const { format: formatCurrency } = useFormatCurrency();

  // Fetch all calculators to determine the date range for visits
  const { data: calculators } = useAllPricingCalculatorsQuery();

  // Calculate the date range that covers all calculators
  const allCalculatorsDateRange = useMemo(() => {
    if (!calculators || calculators.length === 0) {
      return { from: dayjs(), to: dayjs() };
    }

    const dates = calculators.map(calc => dayjs(calc.calculationMonth).valueOf());
    const minTime = Math.min(...dates);
    const maxTime = Math.max(...dates);

    return {
      from: dayjs(minTime).startOf('month'),
      to: dayjs(maxTime).endOf('month'),
    };
  }, [calculators]);

  const { data: allVisits } = useSuspenseVisitsQuery({
    query: allCalculatorsDateRange,
  });

  if (visits.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <FormattedMessage id="pricing.metrics.title" defaultMessage="Obchodní analýza" />
        </Typography>
        <Alert severity="info">
          <FormattedMessage
            id="pricing.metrics.noData"
            defaultMessage="Pro zobrazení metrik je potřeba mít záznamy návštěv v daném období."
          />
        </Alert>
      </Paper>
    );
  }
  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <FormattedMessage id="pricing.metrics.title" defaultMessage="Obchodní analýza" />
        </Typography>

        {/* Quick Summary Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          {/* Zisk */}
          <Paper
            sx={{
              p: 2,
              flex: 1,
              backgroundColor: metrics.summary.totalProfit >= 0 ? 'success.light' : 'error.light',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              {metrics.summary.totalProfit >= 0 ? (
                <CheckCircle color="success" />
              ) : (
                <Warning color="error" />
              )}
              <Typography variant="subtitle2">
                <FormattedMessage id="pricing.metrics.totalProfit" defaultMessage="Celkový zisk" />
              </Typography>
            </Stack>
            <Typography
              variant="h5"
              color={metrics.summary.totalProfit >= 0 ? 'success.dark' : 'error.dark'}
            >
              {formatCurrency(metrics.summary.totalProfit)}
            </Typography>
          </Paper>

          {/* Tržby */}
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              <FormattedMessage id="pricing.metrics.revenue" defaultMessage="Tržby" />
            </Typography>
            <Typography variant="h5" color="primary">
              {formatCurrency(metrics.summary.totalRevenue)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {metrics.loyaltyPoints.totalVisits}{' '}
              <FormattedMessage id="pricing.metrics.visits" defaultMessage="návštěv" />
            </Typography>
          </Paper>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Break-even analýza */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <FormattedMessage id="pricing.metrics.breakeven" defaultMessage="Analýza bodu zvratu" />
          </Typography>
          <Stack direction="row" spacing={2}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage id="pricing.fixedCosts" defaultMessage="Stálé náklady" />
              </Typography>
              <Typography variant="body1">
                {formatCurrency(metrics.breakEven.fixedCosts)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage id="pricing.metrics.variableCosts" />
              </Typography>
              <Typography variant="body1">
                {formatCurrency(metrics.breakEven.variableCosts)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Neefektivní služby */}
        {metrics.insights.servicesNeedingAttention.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <FormattedMessage
                id="pricing.metrics.inefficientServices"
                defaultMessage="Služby vyžadující pozornost"
              />
            </Typography>
            <Stack spacing={1}>
              {metrics.insights.servicesNeedingAttention.slice(0, 3).map(service => (
                <Paper key={service.serviceId} sx={{ p: 1.5, backgroundColor: 'warning.light' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Warning color="warning" fontSize="small" />
                      <Typography variant="body2">{service.serviceName}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      <FormattedMessage
                        id="pricing.metrics.inefficiency.description"
                        defaultMessage="{time}% času, {profit}% zisku"
                        values={{
                          time: service.timeSharePercentage.toFixed(1),
                          profit: service.profitSharePercentage.toFixed(1),
                        }}
                      />
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="warning.dark">
                    <FormattedMessage
                      id="pricing.metrics.inefficiency.loss"
                      defaultMessage="Potenciální ztráta: {amount}"
                      values={{ amount: formatCurrency(service.financialLoss) }}
                    />
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Nové vs. stálé zákaznice */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <FormattedMessage
              id="pricing.metrics.customers"
              defaultMessage="Nové vs. stálé zákaznice"
            />
          </Typography>
          <Stack direction="row" spacing={2}>
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center', backgroundColor: 'success.light' }}>
              <Typography variant="h4" color="success.dark">
                {clientsRetention?.newCustomers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <FormattedMessage id="pricing.metrics.newCustomers" defaultMessage="Nové" /> (
                {formatPercentage(clientsRetention?.newCustomersPercentage || 0)})
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center', backgroundColor: 'info.light' }}>
              <Typography variant="h4" color="info.dark">
                {clientsRetention?.returningCustomers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <FormattedMessage id="pricing.metrics.returning" defaultMessage="Stálé" /> (
                {formatPercentage(clientsRetention?.returningCustomersPercentage || 0)})
              </Typography>
            </Paper>
          </Stack>
        </Box>

        {/* Věrnostní program */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <FormattedMessage
              id="pricing.metrics.loyaltyProgram"
              defaultMessage="Věrnostní program"
            />
          </Typography>
          <Stack direction="row" spacing={2}>
            <Box flex={1}>
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage
                  id="pricing.metrics.loyalty.earned"
                  defaultMessage="Vydané body"
                />
              </Typography>
              <Typography variant="body1">
                {`${metrics.loyaltyPoints.totalEarnedValue} bodů`}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage
                  id="pricing.metrics.loyalty.redeemed"
                  defaultMessage="Uplatněné body"
                />
              </Typography>
              <Typography variant="body1">
                {`${metrics.loyaltyPoints.totalRedeemedValue} bodů`}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage
                  id="pricing.metrics.loyalty.percentage"
                  defaultMessage="Návštěv s body"
                />
              </Typography>
              <Typography variant="body1">
                {formatPercentage(metrics.loyaltyPoints.visitsWithPointsRedeemedPercentage)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>
      <MonthlyTrendChart allVisits={allVisits} />
    </>
  );
};

// Samostatná komponenta pro detailní metriky služeb
export const ServiceDetailedMetricsSection: React.FC<BusinessMetricsSectionProps> = ({
  visits,
  overheadCosts,
  dateRange,
}) => {
  const metrics = useBusinessMetricsData({ visits, overheadCosts, dateRange });
  const { format: formatCurrency } = useFormatCurrency();

  if (visits.length === 0) {
    return null;
  }

  return (
    <ServiceDetailedMetricsTable
      services={metrics.serviceDetailedMetrics}
      formatCurrency={formatCurrency}
      formatPercentage={formatPercentage}
    />
  );
};

export default BusinessMetricsSection;
